// Wrappers around the FastAPI feedback + chats endpoints, proxied through
// Django at /api/fastapi/...
//
// The proxy strips end-user cookies, so authentication-relevant data is
// passed via headers (Django session cookie for auth, X-Client-Id for
// row-level authorization). apiFetch handles both automatically.

import { apiFetch } from './api';

export interface FeedbackPayload {
  turn_id: string;
  relevance?: number | null;
  correctness?: number | null;
  completeness?: number | null;
  comment?: string | null;
  what_went_well?: string | null;
  what_didnt?: string | null;
  meta?: Record<string, unknown> | null;
}

export interface FeedbackRecord {
  turn_id: string;
  client_id: string;
  relevance: number | null;
  correctness: number | null;
  completeness: number | null;
  comment: string | null;
  what_went_well: string | null;
  what_didnt: string | null;
  meta: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface ChatSummary {
  chat_id: string;
  title: string | null;
  is_sensitive: boolean;
  folder_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface TurnRecord {
  turn_id: string;
  chat_id: string;
  seq: number;
  query: string;
  answer: string;
  plan: Record<string, unknown> | null;
  sources: Array<Record<string, unknown>> | null;
  model_name: string | null;
  latency_ms: number | null;
  // Per-turn pipeline observability fields persisted by the server.
  // Each is independent (own JSON column) so future analytics can
  // json_extract any one of them without parsing the others.
  routing_event: Record<string, unknown> | null;
  planning_event: Record<string, unknown> | null;
  profile_event: Record<string, unknown> | null;
  graph_traversal: { nodes: unknown[]; edges: unknown[] } | null;
  step_details: {
    completed_steps: string[];
    step_details: unknown[];
    score_threshold: Record<string, unknown> | null;
  } | null;
  detected_iocs: { query: unknown[]; response: unknown[] } | null;
  created_at: string;
  feedback: FeedbackRecord | null;
}

export interface ChatDetail {
  chat_id: string;
  client_id: string;
  title: string | null;
  is_sensitive: boolean;
  folder_id: string | null;
  accumulated_graph: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  turns: TurnRecord[];
  /** The chat's still-running turn (background run), if any. */
  active_run?: {
    run_id: string;
    turn_id: string;
    chat_id: string | null;
    status: string;
    query: string;
    last_seq: number;
  } | null;
}

export interface LowRatedTurn {
  turn_id: string;
  chat_id: string;
  chat_title: string | null;
  client_id: string;
  seq: number;
  query: string;
  answer: string;
  plan: Record<string, unknown> | null;
  sources: Array<Record<string, unknown>> | null;
  model_name: string | null;
  latency_ms: number | null;
  relevance: number | null;
  correctness: number | null;
  completeness: number | null;
  comment: string | null;
  what_went_well: string | null;
  what_didnt: string | null;
  mean_rating: number | null;
  feedback_updated_at: string;
  feedback_created_at: string;
  turn_created_at: string;
}

export type FeedbackOrderBy =
  | 'feedback_updated_at'
  | 'feedback_created_at'
  | 'turn_created_at'
  | 'mean_rating';

export type FeedbackOrderDir = 'asc' | 'desc';

export function postFeedback(body: FeedbackPayload): Promise<FeedbackRecord> {
  return apiFetch<FeedbackRecord>('/api/fastapi/feedback', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getFeedback(turn_id: string): Promise<FeedbackRecord | null> {
  return apiFetch<FeedbackRecord>(
    `/api/fastapi/feedback/${encodeURIComponent(turn_id)}`,
  ).catch((e: { status?: number }) => (e?.status === 404 ? null : Promise.reject(e)));
}

export function listLowRated(params: {
  max_rating?: number;
  since?: string;
  until?: string;
  order_by?: FeedbackOrderBy;
  order_dir?: FeedbackOrderDir;
  limit?: number;
}): Promise<LowRatedTurn[]> {
  const qs = new URLSearchParams();
  if (params.max_rating != null) qs.set('max_rating', String(params.max_rating));
  if (params.since) qs.set('since', params.since);
  if (params.until) qs.set('until', params.until);
  if (params.order_by) qs.set('order_by', params.order_by);
  if (params.order_dir) qs.set('order_dir', params.order_dir);
  if (params.limit != null) qs.set('limit', String(params.limit));
  const suffix = qs.toString();
  return apiFetch<LowRatedTurn[]>(
    `/api/fastapi/feedback${suffix ? '?' + suffix : ''}`,
  );
}

export function listChats(): Promise<ChatSummary[]> {
  return apiFetch<ChatSummary[]>('/api/fastapi/chats');
}

export function getChat(chat_id: string): Promise<ChatDetail> {
  return apiFetch<ChatDetail>(`/api/fastapi/chats/${encodeURIComponent(chat_id)}`);
}

export function patchChat(
  chat_id: string,
  body: { title?: string; is_sensitive?: boolean },
): Promise<ChatSummary> {
  return apiFetch<ChatSummary>(`/api/fastapi/chats/${encodeURIComponent(chat_id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteChat(chat_id: string): Promise<{ deleted: number }> {
  return apiFetch<{ deleted: number }>(
    `/api/fastapi/chats/${encodeURIComponent(chat_id)}`,
    { method: 'DELETE' },
  );
}

/** Full-replace, last-write-wins. Pass `null` to clear. Debounced from the SPA
 *  when the per-session investigation graph mutates. */
export function updateAccumulatedGraph(
  chat_id: string,
  accumulated_graph: Record<string, unknown> | null,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/fastapi/chats/${encodeURIComponent(chat_id)}/accumulated-graph`,
    {
      method: 'PATCH',
      body: JSON.stringify({ accumulated_graph }),
    },
  );
}

export function deleteTurn(turn_id: string): Promise<{ deleted: number }> {
  return apiFetch<{ deleted: number }>(
    `/api/fastapi/turns/${encodeURIComponent(turn_id)}`,
    { method: 'DELETE' },
  );
}

// Bulk migration of localStorage chat history → server. The server endpoint
// `/chats/import` is not yet implemented; the call is stubbed so chatBackfill
// type-checks. chatBackfill swallows the failure on every load.
export interface ImportTurnPayload {
  turn_id: string;
  seq: number;
  query: string;
  answer: string;
  sources: unknown;
  plan: unknown;
  model_name: string | null;
  latency_ms?: number | null;
  routing_event?: unknown;
  planning_event?: unknown;
  profile_event?: unknown;
  graph_traversal?: unknown;
  step_details?: unknown;
  detected_iocs?: unknown;
  created_at: string;
}

export interface ImportChatPayload {
  chat_id: string;
  title: string;
  is_sensitive: boolean;
  folder_id?: string | null;
  accumulated_graph?: unknown;
  created_at: string;
  updated_at: string;
  turns: ImportTurnPayload[];
}

export interface ImportFolderPayload {
  folder_id: string;
  name: string;
  created_at: string;
}

export interface ImportChatsRequest {
  chats: ImportChatPayload[];
  folders?: ImportFolderPayload[];
  folder_collapse?: Record<string, boolean>;
}

export interface ImportChatsResult {
  imported: number;
  skipped: number;
}

export function importChats(body: ImportChatsRequest): Promise<ImportChatsResult> {
  return apiFetch<ImportChatsResult>('/api/fastapi/chats/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// ── Folder organization ───────────────────────────────────────────────────

export interface FolderRecord {
  folder_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export function listFolders(): Promise<FolderRecord[]> {
  return apiFetch<FolderRecord[]>('/api/fastapi/folders');
}

export function createFolder(body: {
  folder_id: string;
  name: string;
}): Promise<FolderRecord> {
  return apiFetch<FolderRecord>('/api/fastapi/folders', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function patchFolder(
  folder_id: string,
  body: { name: string },
): Promise<FolderRecord> {
  return apiFetch<FolderRecord>(
    `/api/fastapi/folders/${encodeURIComponent(folder_id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export function deleteFolder(folder_id: string): Promise<{ deleted: number }> {
  return apiFetch<{ deleted: number }>(
    `/api/fastapi/folders/${encodeURIComponent(folder_id)}`,
    { method: 'DELETE' },
  );
}

export function setChatFolder(
  chat_id: string,
  folder_id: string | null,
): Promise<ChatSummary> {
  return apiFetch<ChatSummary>(
    `/api/fastapi/chats/${encodeURIComponent(chat_id)}/folder`,
    { method: 'PATCH', body: JSON.stringify({ folder_id }) },
  );
}

export function getFolderCollapse(): Promise<Record<string, boolean>> {
  return apiFetch<Record<string, boolean>>('/api/fastapi/folder-collapse');
}

export function setFolderCollapse(
  folder_id: string,
  collapsed: boolean,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/fastapi/folder-collapse/${encodeURIComponent(folder_id)}`,
    { method: 'PUT', body: JSON.stringify({ collapsed }) },
  );
}
